import { create } from 'zustand';
import { Mindspace, ContextItem, fetchMindspaces, fetchContextItems, createContextItem, CreateContextItemRequest } from '../services/apiClientV2';
import { logDebug, logError } from '../utils/logging';
import { trackEvent } from '../services/analyticsService';
import useAuthStore from './authStore';

interface MindspaceState {
  // State
  defaultMindspace: Mindspace | null;
  contextItems: ContextItem[];
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  retryCount: number;
  canRetry: boolean;

  // Actions
  loadDefaultMindspace: () => Promise<void>;
  retryMindspaceInitialization: () => Promise<void>;
  _loadDefaultMindspaceInternal: () => Promise<void>;
  loadContextItems: (mindspaceId: string) => Promise<void>;
  updateContextItem: (key: string, content: string) => Promise<void>;
  clearError: () => void;

  // Helper methods for better integration
  getContextValue: (key: string) => string;
  hasContextValue: (key: string) => boolean;
  updateMultipleContextItems: (contextUpdates: Record<string, string>) => Promise<void>;
}

export const useMindspaceStore = create<MindspaceState>((set, get) => ({
  // Initial state
  defaultMindspace: null,
  contextItems: [],
  isLoading: false,
  error: null,
  isInitialized: false,
  retryCount: 0,
  canRetry: true,

  // Load default mindspace (auto-creates if none exists)
  loadDefaultMindspace: async () => {
    const state = get();
    const { isGuest } = useAuthStore.getState();

    // Guests do not have mindspaces; mark initialized and skip loading
    if (isGuest) {
      logDebug('[mindspaceStore] Guest mode detected – skipping mindspace load');
      set({ isInitialized: true, defaultMindspace: null, contextItems: [] });
      return;
    }

    if (state.isInitialized && state.defaultMindspace) {
      logDebug('[mindspaceStore] Default mindspace already loaded');
      return;
    }

    return get()._loadDefaultMindspaceInternal();
  },

  // Retry mindspace initialization with user action
  retryMindspaceInitialization: async () => {
    const state = get();
    if (!state.canRetry) {
      logDebug('[mindspaceStore] Retry not allowed (circuit breaker active)');
      return;
    }

    logDebug('[mindspaceStore] User initiated retry for mindspace initialization');
    set({ error: null, retryCount: 0, canRetry: true });
    
    trackEvent('mindspace_retry_initiated', {
      retry_count: state.retryCount
    });

    return get()._loadDefaultMindspaceInternal();
  },

  // Internal method with retry logic and exponential backoff
  _loadDefaultMindspaceInternal: async () => {
    const MAX_RETRIES = 3;
    const state = get();
    
    // Circuit breaker - prevent infinite retries
    if (state.retryCount >= MAX_RETRIES) {
      const errorMessage = 'Nu am putut inițializa contextul după multiple încercări. Vă rugăm să reîncărcați pagina sau să contactați suportul.';
      set({ 
        isLoading: false, 
        error: errorMessage,
        isInitialized: true,
        canRetry: false
      });
      return;
    }

    // Performance monitoring - start timer
    const startTime = performance.now();
    set({ isLoading: true, error: null });

    try {
      // Exponential backoff delay for retries
      if (state.retryCount > 0) {
        const delay = Math.min(1000 * Math.pow(2, state.retryCount - 1), 5000); // Max 5s delay
        logDebug(`[mindspaceStore] Retry ${state.retryCount}/${MAX_RETRIES} after ${delay}ms delay`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      logDebug('[mindspaceStore] Loading default mindspace...');
      const mindspaces = await fetchMindspaces();
      
      // Find default mindspace or use first one
      const defaultMindspace = mindspaces.find(m => m.is_default) || mindspaces[0] || null;
      
      if (defaultMindspace) {
        // Performance monitoring - calculate initialization time
        const initializationTime = performance.now() - startTime;
        logDebug(`[mindspaceStore] Default mindspace loaded in ${initializationTime.toFixed(2)}ms:`, defaultMindspace.id);
        
        // Track successful mindspace initialization with performance metrics
        trackEvent('mindspace_initialized', {
          mindspace_id: defaultMindspace.id,
          is_new: mindspaces.length === 1, // Assume new if only one mindspace
          initialization_time_ms: Math.round(initializationTime),
          performance_target_met: initializationTime < 300,
          retry_count: state.retryCount
        });

        // Performance warning if target not met
        if (initializationTime > 300) {
          logError(`[mindspaceStore] Performance warning: Mindspace initialization took ${initializationTime.toFixed(2)}ms (target: <300ms)`);
        }

        set({ 
          defaultMindspace, 
          isLoading: false, 
          isInitialized: true,
          error: null,
          retryCount: 0,
          canRetry: true
        });

        // Auto-load context items for the default mindspace
        await get().loadContextItems(defaultMindspace.id);
      } else {
        throw new Error('No mindspace available');
      }
    } catch (error) {
      const initializationTime = performance.now() - startTime;
      const currentRetryCount = state.retryCount + 1;
      
      let errorMessage: string;
      if (currentRetryCount >= MAX_RETRIES) {
        errorMessage = 'Nu am putut inițializa contextul după multiple încercări. Vă rugăm să reîncărcați pagina sau să verificați conexiunea la internet.';
      } else {
        errorMessage = `Inițializarea contextului a eșuat (încercarea ${currentRetryCount}/${MAX_RETRIES}). Se reîncearcă automat...`;
      }
      
      logError('[mindspaceStore] Error loading default mindspace:', error);
      
      // Track mindspace initialization failure with performance metrics
      trackEvent('mindspace_init_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        initialization_time_ms: Math.round(initializationTime),
        retry_count: currentRetryCount,
        will_retry: currentRetryCount < MAX_RETRIES
      });

      set({ 
        isLoading: false, 
        error: errorMessage,
        isInitialized: currentRetryCount >= MAX_RETRIES,
        retryCount: currentRetryCount,
        canRetry: currentRetryCount < MAX_RETRIES
      });

      // Automatic retry with exponential backoff
      if (currentRetryCount < MAX_RETRIES) {
        setTimeout(() => {
          get()._loadDefaultMindspaceInternal();
        }, 100); // Small delay before retry
      }
    }
  },

  // Load context items for a mindspace
  loadContextItems: async (mindspaceId: string) => {
    try {
      logDebug('[mindspaceStore] Loading context items for mindspace:', mindspaceId);
      const contextItems = await fetchContextItems(mindspaceId);
      
      logDebug('[mindspaceStore] Context items loaded:', contextItems.length);
      set({ contextItems });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load context items';
      logError('[mindspaceStore] Error loading context items:', error);
      set({ error: errorMessage });
    }
  },

  // Update or create a context item (upsert for canonical keys)
  updateContextItem: async (key: string, content: string) => {
    const state = get();
    if (!state.defaultMindspace) {
      throw new Error('No default mindspace available');
    }

    try {
      logDebug('[mindspaceStore] Updating context item:', key, content);
      
      const contextItemRequest: CreateContextItemRequest = {
        item_type: key,
        content: content.trim(),
        metadata: {}
      };

      const updatedItem = await createContextItem(state.defaultMindspace.id, contextItemRequest);
      
      // Update local state optimistically
      const existingItemIndex = state.contextItems.findIndex(item => item.item_type === key);
      let newContextItems: ContextItem[];
      
      if (existingItemIndex >= 0) {
        // Update existing item
        newContextItems = [...state.contextItems];
        newContextItems[existingItemIndex] = updatedItem;
      } else {
        // Add new item
        newContextItems = [...state.contextItems, updatedItem];
      }

      set({ contextItems: newContextItems });

      // Track context update
      trackEvent('context_item_updated', {
        mindspace_id: state.defaultMindspace.id,
        item_type: key,
        content_length: content.length
      });

      logDebug('[mindspaceStore] Context item updated successfully:', updatedItem);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update context item';
      logError('[mindspaceStore] Error updating context item:', error);
      
      // Track context update failure
      trackEvent('context_item_update_failed', {
        item_type: key,
        error: errorMessage
      });

      set({ error: errorMessage });
      throw error; // Re-throw for component error handling
    }
  },

  // Clear error state
  clearError: () => {
    set({ error: null });
  },

  // Helper methods for better integration
  getContextValue: (key: string) => {
    const state = get();
    const item = state.contextItems.find(item => item.item_type === key);
    return item?.content || '';
  },

  hasContextValue: (key: string) => {
    const state = get();
    return state.contextItems.some(item => item.item_type === key && item.content.trim() !== '');
  },

  // Batch update multiple context items (for guided flow integration)
  updateMultipleContextItems: async (contextUpdates: Record<string, string>) => {
    const state = get();
    if (!state.defaultMindspace) {
      throw new Error('No default mindspace available');
    }

    const promises = Object.entries(contextUpdates).map(([key, content]) => {
      if (content.trim() !== '') {
        return get().updateContextItem(key, content);
      }
      return Promise.resolve();
    });

    try {
      await Promise.all(promises);
      logDebug('[mindspaceStore] Batch context update completed');
    } catch (error) {
      logError('[mindspaceStore] Error in batch context update:', error);
      throw error;
    }
  },
}));

export default useMindspaceStore; 